import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Spinner,
  Alert,
  Badge,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
  Form,
} from "reactstrap";
import { toast } from "react-toastify";
import HeadersRole from "../../../Components/Headers/HeadersRole";
import {
  getPromoCodes,
  createPromoCode,
  updatePromoCode,
  togglePromoCode,
  getPromoCodeRedemptions,
} from "../../../slices/admin/thunk";
import { formatDate } from "../../../utils/utils";
import scheduleIcon from "../../../assets/images/iconsRoles/nationalIcon.svg";
import FooterPagination from "../../../Components/Pagination/FooterPagination";

const DISCOUNT_TYPES = [
  { value: "percentage", label: "Percentage" },
  { value: "first_month_free", label: "First Month Free" },
  { value: "fixed_amount", label: "Fixed Amount" },
];

const DEFAULT_FORM = {
  code: "",
  description: "",
  discountType: "percentage",
  discountValue: 0,
  firstPaymentOnly: false,
  maxRedemptions: "",
  expiresAt: "",
  onePerUser: true,
};

const PromoCodesManagement = () => {
  const dispatch = useDispatch();
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("");

  // Create/Edit modal
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [editingPromoId, setEditingPromoId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Redemptions modal
  const [redemptionsModalOpen, setRedemptionsModalOpen] = useState(false);
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionsLoading, setRedemptionsLoading] = useState(false);
  const [redemptionsPromo, setRedemptionsPromo] = useState(null);
  const [redemptionsPage, setRedemptionsPage] = useState(0);
  const [redemptionsHasMore, setRedemptionsHasMore] = useState(false);

  // Toggle state
  const [togglingId, setTogglingId] = useState(null);

  const fetchPromoCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page: currentPage, limit: pageSize };
      if (activeFilter) params.active = activeFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await dispatch(getPromoCodes(params)).unwrap();
      setPromoCodes(response.data || []);
      setTotal(response.total || 0);
    } catch (err) {
      setError(err.message || "Failed to fetch promo codes");
      console.error("Error fetching promo codes:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, currentPage, pageSize, activeFilter, searchQuery]);

  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  // Search with debounce
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(0);
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  // Form handlers
  const handleOpenCreateModal = () => {
    setFormData(DEFAULT_FORM);
    setEditingPromoId(null);
    setFormModalOpen(true);
  };

  const handleOpenEditModal = (promo) => {
    setFormData({
      code: promo.code || "",
      description: promo.description || "",
      discountType: promo.discountType || "percentage",
      discountValue: promo.discountType === "fixed_amount"
        ? (promo.discountValue / 100)
        : promo.discountValue || 0,
      firstPaymentOnly: promo.firstPaymentOnly || false,
      maxRedemptions: promo.maxRedemptions || "",
      expiresAt: promo.expiresAt
        ? new Date(promo.expiresAt).toISOString().split("T")[0]
        : "",
      onePerUser: promo.onePerUser !== false,
    });
    setEditingPromoId(promo.id);
    setFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setFormModalOpen(false);
    setEditingPromoId(null);
    setFormData(DEFAULT_FORM);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        code: formData.code.toUpperCase().trim(),
        description: formData.description,
        discountType: formData.discountType,
        discountValue: formData.discountType === "first_month_free"
          ? 0
          : formData.discountType === "fixed_amount"
            ? Math.round(Number(formData.discountValue) * 100)
            : Number(formData.discountValue),
        firstPaymentOnly: formData.discountType === "first_month_free"
          ? true
          : formData.firstPaymentOnly,
        maxRedemptions: formData.maxRedemptions
          ? Number(formData.maxRedemptions)
          : null,
        expiresAt: formData.expiresAt
          ? new Date(formData.expiresAt).getTime()
          : null,
        onePerUser: formData.onePerUser,
      };

      if (editingPromoId) {
        await dispatch(updatePromoCode({ id: editingPromoId, ...payload })).unwrap();
        toast.success("Promo code updated successfully");
      } else {
        await dispatch(createPromoCode(payload)).unwrap();
        toast.success("Promo code created successfully");
      }

      handleCloseFormModal();
      fetchPromoCodes();
    } catch (err) {
      toast.error(err.message || "Failed to save promo code");
    } finally {
      setSaving(false);
    }
  };

  // Toggle active/inactive
  const handleToggle = async (promo) => {
    setTogglingId(promo.id);
    try {
      await dispatch(togglePromoCode({ id: promo.id, active: !promo.active })).unwrap();
      toast.success(`Promo code ${promo.active ? "deactivated" : "activated"} successfully`);
      fetchPromoCodes();
    } catch (err) {
      toast.error(err.message || "Failed to toggle promo code");
    } finally {
      setTogglingId(null);
    }
  };

  // Redemptions
  const handleOpenRedemptions = async (promo) => {
    setRedemptionsPromo(promo);
    setRedemptionsPage(0);
    setRedemptionsModalOpen(true);
    await fetchRedemptions(promo.id, 0);
  };

  const fetchRedemptions = async (promoId, page) => {
    setRedemptionsLoading(true);
    try {
      const response = await dispatch(
        getPromoCodeRedemptions({ id: promoId, page, limit: 20 })
      ).unwrap();
      setRedemptions(response.data || []);
      setRedemptionsHasMore(response.hasMore || false);
    } catch (err) {
      toast.error("Failed to load redemptions");
    } finally {
      setRedemptionsLoading(false);
    }
  };

  const handleCloseRedemptions = () => {
    setRedemptionsModalOpen(false);
    setRedemptionsPromo(null);
    setRedemptions([]);
  };

  // Discount display
  const formatDiscount = (promo) => {
    switch (promo.discountType) {
      case "percentage":
        return `${promo.discountValue}% off`;
      case "first_month_free":
        return "First month free";
      case "fixed_amount":
        return `$${(promo.discountValue / 100).toFixed(2)} off`;
      default:
        return promo.discountValue;
    }
  };

  const getDiscountBadgeColor = (type) => {
    switch (type) {
      case "percentage": return "primary";
      case "first_month_free": return "success";
      case "fixed_amount": return "info";
      default: return "secondary";
    }
  };

  const headerLabel = (
    <span className="text-muted">
      Create and manage promotional codes
    </span>
  );

  return (
    <div className="page-content">
      <HeadersRole
        role={"Promo Codes"}
        icon={scheduleIcon}
        svg={true}
        text={headerLabel}
      />

      <Container fluid>
        {/* Filters & Actions */}
        <Row className="mb-3">
          <Col>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div style={{ minWidth: "250px" }}>
                      <Input
                        type="text"
                        placeholder="Search by code or description..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                    </div>
                    <div>
                      <Input
                        type="select"
                        value={activeFilter}
                        onChange={(e) => {
                          setActiveFilter(e.target.value);
                          setCurrentPage(0);
                        }}
                        style={{ width: "150px" }}
                      >
                        <option value="">All Status</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </Input>
                    </div>
                  </div>
                  <div className="d-flex gap-2">
                    <Button color="primary" size="sm" onClick={fetchPromoCodes} disabled={loading}>
                      <i className="ri-refresh-line me-1"></i>
                      Refresh
                    </Button>
                    <Button color="success" onClick={handleOpenCreateModal}>
                      <i className="ri-add-line me-1"></i>
                      Create Promo Code
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Error */}
        {error && (
          <Alert color="danger">
            <i className="ri-error-warning-line me-2"></i>
            {error}
          </Alert>
        )}

        {/* Promo Codes Table */}
        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                  Promo Codes
                  {total > 0 && (
                    <Badge color="secondary" className="ms-2">{total}</Badge>
                  )}
                </h5>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                  </div>
                ) : promoCodes.length === 0 ? (
                  <Alert color="info" className="mb-0">
                    <i className="ri-information-line me-2"></i>
                    No promo codes found.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover table-nowrap align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Code</th>
                          <th>Description</th>
                          <th>Discount</th>
                          <th>Type</th>
                          <th>Redemptions</th>
                          <th>Status</th>
                          <th>Expires</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {promoCodes.map((promo) => (
                          <tr key={promo.id}>
                            <td>
                              <strong className="font-monospace">{promo.code}</strong>
                            </td>
                            <td>
                              <small>{promo.description || "-"}</small>
                            </td>
                            <td>
                              <Badge color={getDiscountBadgeColor(promo.discountType)}>
                                {formatDiscount(promo)}
                              </Badge>
                            </td>
                            <td>
                              <small>
                                {promo.firstPaymentOnly ? (
                                  <Badge color="warning" className="text-dark">First payment only</Badge>
                                ) : (
                                  <Badge color="secondary">Ongoing</Badge>
                                )}
                              </small>
                            </td>
                            <td>
                              <span
                                className="text-primary cursor-pointer"
                                style={{ textDecoration: "underline" }}
                                onClick={() => handleOpenRedemptions(promo)}
                              >
                                {promo.totalRedemptions || 0}
                                {promo.maxRedemptions ? ` / ${promo.maxRedemptions}` : ""}
                              </span>
                            </td>
                            <td>
                              <Badge color={promo.active ? "success" : "danger"}>
                                {promo.active ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                            <td>
                              {promo.expiresAt ? (
                                <small>
                                  {formatDate(promo.expiresAt)}
                                  {promo.expiresAt < Date.now() && (
                                    <Badge color="danger" className="ms-1">Expired</Badge>
                                  )}
                                </small>
                              ) : (
                                <small className="text-muted">Never</small>
                              )}
                            </td>
                            <td className="text-center">
                              <div className="d-flex gap-1 justify-content-center">
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  onClick={() => handleOpenEditModal(promo)}
                                  title="Edit"
                                >
                                  <i className="ri-pencil-line"></i>
                                </Button>
                                <Button
                                  color={promo.active ? "soft-danger" : "soft-success"}
                                  size="sm"
                                  onClick={() => handleToggle(promo)}
                                  disabled={togglingId === promo.id}
                                  title={promo.active ? "Deactivate" : "Activate"}
                                >
                                  {togglingId === promo.id ? (
                                    <Spinner size="sm" />
                                  ) : promo.active ? (
                                    <i className="ri-close-circle-line"></i>
                                  ) : (
                                    <i className="ri-checkbox-circle-line"></i>
                                  )}
                                </Button>
                                <Button
                                  color="soft-info"
                                  size="sm"
                                  onClick={() => handleOpenRedemptions(promo)}
                                  title="View Redemptions"
                                >
                                  <i className="ri-eye-line"></i>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {total > pageSize && (
                        <tfoot>
                          <FooterPagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(total / pageSize)}
                            totalItems={total}
                            pageSize={pageSize}
                            onChangePage={setCurrentPage}
                          />
                        </tfoot>
                      )}
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Create / Edit Modal */}
      <Modal isOpen={formModalOpen} toggle={handleCloseFormModal} centered size="lg">
        <ModalHeader toggle={handleCloseFormModal}>
          {editingPromoId ? "Edit Promo Code" : "Create Promo Code"}
        </ModalHeader>
        <Form onSubmit={handleSubmitForm}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Code *</Label>
                  <Input
                    type="text"
                    value={formData.code}
                    onChange={(e) => handleFormChange("code", e.target.value)}
                    placeholder="e.g. SAVE20"
                    required
                    disabled={!!editingPromoId}
                    style={{ textTransform: "uppercase" }}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Discount Type *</Label>
                  <Input
                    type="select"
                    value={formData.discountType}
                    onChange={(e) => handleFormChange("discountType", e.target.value)}
                  >
                    {DISCOUNT_TYPES.map((dt) => (
                      <option key={dt.value} value={dt.value}>{dt.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label>Description</Label>
              <Input
                type="text"
                value={formData.description}
                onChange={(e) => handleFormChange("description", e.target.value)}
                placeholder="e.g. Summer discount 2025"
              />
            </FormGroup>

            {formData.discountType !== "first_month_free" && (
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>
                      {formData.discountType === "percentage"
                        ? "Discount Value (%)"
                        : "Discount Value ($)"}
                    </Label>
                    <Input
                      type="number"
                      value={formData.discountValue}
                      onChange={(e) => handleFormChange("discountValue", e.target.value)}
                      min={0}
                      max={formData.discountType === "percentage" ? 100 : undefined}
                      step={formData.discountType === "fixed_amount" ? "0.01" : "1"}
                      required
                    />
                    {formData.discountType === "fixed_amount" && (
                      <small className="text-muted">Enter amount in dollars (e.g. 5.00 = $5.00 off)</small>
                    )}
                    {formData.discountType === "percentage" && (
                      <small className="text-muted">Enter percentage (e.g. 25 = 25% off)</small>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Max Redemptions</Label>
                  <Input
                    type="number"
                    value={formData.maxRedemptions}
                    onChange={(e) => handleFormChange("maxRedemptions", e.target.value)}
                    placeholder="Unlimited"
                    min={1}
                  />
                  <small className="text-muted">Leave empty for unlimited</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Expires At</Label>
                  <Input
                    type="date"
                    value={formData.expiresAt}
                    onChange={(e) => handleFormChange("expiresAt", e.target.value)}
                  />
                  <small className="text-muted">Leave empty for no expiration</small>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup check className="mt-2">
                  <Input
                    type="checkbox"
                    id="firstPaymentOnly"
                    checked={formData.discountType === "first_month_free" ? true : formData.firstPaymentOnly}
                    onChange={(e) => handleFormChange("firstPaymentOnly", e.target.checked)}
                    disabled={formData.discountType === "first_month_free"}
                  />
                  <Label check htmlFor="firstPaymentOnly">
                    First payment only
                  </Label>
                  <br />
                  <small className="text-muted">If checked, discount applies only to the first payment</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup check className="mt-2">
                  <Input
                    type="checkbox"
                    id="onePerUser"
                    checked={formData.onePerUser}
                    onChange={(e) => handleFormChange("onePerUser", e.target.checked)}
                  />
                  <Label check htmlFor="onePerUser">
                    One per user
                  </Label>
                  <br />
                  <small className="text-muted">Each user can only use this code once</small>
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={handleCloseFormModal} disabled={saving}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="ri-check-line me-1"></i>
                  {editingPromoId ? "Update" : "Create"}
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Redemptions Modal */}
      <Modal isOpen={redemptionsModalOpen} toggle={handleCloseRedemptions} centered size="lg">
        <ModalHeader toggle={handleCloseRedemptions}>
          Redemptions{redemptionsPromo ? ` — ${redemptionsPromo.code}` : ""}
        </ModalHeader>
        <ModalBody>
          {redemptionsLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : redemptions.length === 0 ? (
            <Alert color="info" className="mb-0">
              No redemptions yet.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table className="table-hover table-nowrap align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>User</th>
                    <th>Discount Applied</th>
                    <th>Redeemed At</th>
                  </tr>
                </thead>
                <tbody>
                  {redemptions.map((r, i) => (
                    <tr key={r.id || i}>
                      <td>
                        <div>
                          <strong>{r.userName || "N/A"}</strong>
                          <br />
                          <small className="text-muted">{r.userEmail}</small>
                        </div>
                      </td>
                      <td>
                        {r.discountAmountAppliedCents != null ? (
                          <span>${(r.discountAmountAppliedCents / 100).toFixed(2)}</span>
                        ) : (
                          <span>{formatDiscount(r)}</span>
                        )}
                      </td>
                      <td>
                        <small>{r.redeemedAt ? formatDate(r.redeemedAt) : "N/A"}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {redemptionsHasMore && (
                <div className="text-center mt-3">
                  <Button
                    color="light"
                    size="sm"
                    onClick={() => {
                      const nextPage = redemptionsPage + 1;
                      setRedemptionsPage(nextPage);
                      fetchRedemptions(redemptionsPromo.id, nextPage);
                    }}
                  >
                    Load More
                  </Button>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleCloseRedemptions}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PromoCodesManagement;
